/**
 * Stack Detection Service — Phase 1
 *
 * Scans uploaded TeamDraft objects and identifies:
 *   - Game stacks: QB + a same-team skill player (WR/TE/RB)
 *   - Orphan QBs: QBs with zero same-team skill players
 *   - Uncorrelated receivers: WR/TE/RB with no QB teammate on any roster slot
 *
 * Bring-back stacks (QB1 + opposing pass-catcher) and run-back stacks
 * require NFL schedule data and are reserved for Phase 4 (2c dependency).
 */

import type { TeamDraft, TeamPlayer } from "../types/draft";

// ── Stack types ───────────────────────────────────────────────────

export type StackType = "game_stack" | "bring_back" | "run_back";

export interface StackedPlayer {
  name: string;
  position: string;
  team: string;
  pickNumber: number;
}

export interface StackEntry {
  type: StackType;
  /** NFL team abbreviation for the primary (QB's) side of the stack */
  nflTeam: string;
  /** NFL team abbreviation for the opposing side (bring-back / run-back only) */
  opposingTeam?: string;
  players: StackedPlayer[];
  /** e.g. "KC game stack: Mahomes + Kelce + Rice" */
  label: string;
  /** Playoff week number this stack applies to (dome/bring-back stacks only) */
  weekNum?: number;
}

export interface TeamStackResult {
  draftId: string;
  stacks: StackEntry[];
  /** True if the team has a QB but zero same-team WR/TE/RB */
  isOrphanQB: boolean;
  orphanQBs: string[];
  /** WR/TE/RB players with no QB on the same NFL team in this draft */
  uncorrelatedReceivers: StackedPlayer[];
  gameStackCount: number;
  bringBackCount: number;
  domeStackCount: number;
  /** Playoff week numbers (15-17) where this team has a dome game stack */
  domeWeeks: number[];
  runBackCount: number;
  totalStackCount: number;
}

// ── Portfolio-level aggregation ───────────────────────────────────

export interface StackPairingRate {
  /** e.g. "Jalen Hurts" */
  qbName: string;
  /** e.g. "DeVonta Smith" */
  receiverName: string;
  nflTeam: string;
  /** Number of teams where both appear together */
  count: number;
  /** Fraction of total teams (0–1) */
  rate: number;
}

export interface PortfolioStackAnalysis {
  totalTeams: number;
  /** % of teams that have at least one game stack */
  gameStackRate: number;
  /** % of teams with a bring-back (0 until schedule data is available) */
  bringBackRate: number;
  /** % of teams with a dome stack in week 17 */
  domeStackRate: number;
  /** % of teams with a run-back (0 until schedule data is available) */
  runBackRate: number;
  /** % of teams with ≥ 1 orphan QB */
  orphanQBRate: number;
  /** Most common QB + receiver pairings across all teams, sorted by count desc */
  topPairings: StackPairingRate[];
  /** Per-team results for downstream use (3b grading, 5a scoring) */
  teamResults: TeamStackResult[];
}

// ── Dome Stadium Configuration ──────────────────────────────────

export const DOME_STADIUMS = new Set([
  "ARI", // Arizona Cardinals - State Farm Stadium
  "DET", // Detroit Lions - Ford Field
  "LV",  // Las Vegas Raiders - Allegiant Stadium
  "LAC", // Los Angeles Chargers - SoFi Stadium
  "LAR", // Los Angeles Rams - SoFi Stadium
  "MIN", // Minnesota Vikings - U.S. Bank Stadium
  "NO",  // New Orleans Saints - Caesars Superdome
  "ATL", // Atlanta Falcons - Mercedes-Benz Stadium (retractable)
  "DAL", // Dallas Cowboys - AT&T Stadium (retractable)
  "HOU", // Houston Texans - NRG Stadium (retractable)
  "IND", // Indianapolis Colts - Lucas Oil Stadium (retractable)
]);

// ── Internal helpers ──────────────────────────────────────────────

function toStackedPlayer(p: TeamPlayer): StackedPlayer {
  return {
    name: p.name,
    position: p.position,
    team: p.team,
    pickNumber: parseInt(p.pickNumber, 10) || 0,
  };
}

// Same-team stack partners: WR/TE plus RB (a QB + his own RB is correlated
// through game script and goal-line/receiving work, and is NOT a naked QB).
const STACK_PARTNER_POSITIONS = new Set(["WR", "TE", "RB"]);
// Bring-back opposing side: any skill player on the opposing team
const BRING_BACK_POSITIONS = new Set(["WR", "TE", "RB"]);

/** Detect game stacks (QB + same-team WR/TE/RB) within one team. */
function detectGameStacks(players: TeamPlayer[]): StackEntry[] {
  const qbs = players.filter(p => p.position === "QB" && p.team);
  const catchers = players.filter(
    p => STACK_PARTNER_POSITIONS.has(p.position) && p.team
  );

  const stacks: StackEntry[] = [];

  for (const qb of qbs) {
    const sameTeamCatchers = catchers.filter(p => p.team === qb.team);
    if (sameTeamCatchers.length === 0) continue;

    const involved: StackedPlayer[] = [toStackedPlayer(qb), ...sameTeamCatchers.map(toStackedPlayer)];
    const names = involved.map(p => p.name).join(" + ");

    stacks.push({
      type: "game_stack",
      nflTeam: qb.team,
      players: involved,
      label: `${qb.team} game stack: ${names}`,
    });
  }

  return stacks;
}

/** 
 * Detect playoff bring-back stacks (QB + same-team skill player + opposing skill player in weeks 15-17).
 * Requires schedule data to identify opposing teams.
 */
function detectPlayoffBringBackStacks(
  players: TeamPlayer[],
  weekNums: number[] | undefined,
  weekSchedules: Record<string, string[]> | undefined
): StackEntry[] {
  if (!weekNums || !weekSchedules) return [];

  const qbs = players.filter(p => p.position === "QB" && p.team);
  const catchers = players.filter(p => STACK_PARTNER_POSITIONS.has(p.position) && p.team);
  const bringBackPool = players.filter(p => BRING_BACK_POSITIONS.has(p.position) && p.team);

  const stacks: StackEntry[] = [];
  const playoffWeekIndices = weekNums
    .map((w, idx) => (w >= 15 && w <= 17 ? idx : -1))
    .filter(idx => idx >= 0);

  for (const qb of qbs) {
    const sameTeamCatchers = catchers.filter(p => p.team === qb.team);
    if (sameTeamCatchers.length === 0) continue;

    // For this QB's team, find opposing teams in playoff weeks
    const qbSchedule = weekSchedules[qb.team];
    if (!qbSchedule) continue;

    for (const weekIdx of playoffWeekIndices) {
      if (weekIdx >= qbSchedule.length) continue;

      const matchupStr = qbSchedule[weekIdx];
      const oppTeam = (matchupStr.startsWith("@")
        ? matchupStr.slice(1)
        : matchupStr.toLowerCase().startsWith("vs")
        ? matchupStr.slice(2)
        : matchupStr  // home game: full string IS the opponent
      ).trim();
      if (!oppTeam || oppTeam.toUpperCase() === "BYE") continue;

      // Check if we have any WR/TE/RB from the opposing team
      const oppCatchers = bringBackPool.filter(p => p.team === oppTeam);
      if (oppCatchers.length === 0) continue;

      // Found a bring-back: QB + same-team catcher + opponent catcher in playoffs
      const involved: StackedPlayer[] = [
        toStackedPlayer(qb),
        ...sameTeamCatchers.map(toStackedPlayer),
        ...oppCatchers.map(toStackedPlayer),
      ];
      const names = involved.map(p => p.name).join(" + ");

      stacks.push({
        type: "bring_back",
        nflTeam: qb.team,
        opposingTeam: oppTeam,
        players: involved,
        label: `${qb.team} bring-back: ${names} (week ${weekNums[weekIdx]})`,
      });
    }
  }

  return stacks;
}

/**
 * Detect playoff dome stacks (game stacks in dome stadiums during weeks 15-17).
 * Requires schedule data to identify which teams play in domes.
 */
function detectPlayoffDomeStacks(
  players: TeamPlayer[],
  weekNums: number[] | undefined,
  weekSchedules: Record<string, string[]> | undefined
): StackEntry[] {
  if (!weekNums || !weekSchedules) return [];

  const gameStacks = detectGameStacks(players);
  const bringBackPool = players.filter(p => BRING_BACK_POSITIONS.has(p.position) && p.team);
  const domeStacks: StackEntry[] = [];
  const playoffWeekIndices = weekNums
    .map((w, idx) => (w >= 15 && w <= 17 ? idx : -1))
    .filter(idx => idx >= 0);

  for (const stack of gameStacks) {
    const teamSchedule = weekSchedules[stack.nflTeam];
    if (!teamSchedule) continue;

    for (const weekIdx of playoffWeekIndices) {
      if (weekIdx >= teamSchedule.length) continue;

      const matchupStr = teamSchedule[weekIdx];
      const oppTeam = (matchupStr.startsWith("@")
        ? matchupStr.slice(1)
        : matchupStr.toLowerCase().startsWith("vs")
        ? matchupStr.slice(2)
        : matchupStr  // home game: full string IS the opponent
      ).trim();
      if (!oppTeam || oppTeam.toUpperCase() === "BYE") continue;

      // Dome only if THIS week's venue is a dome: the stack team's own stadium
      // counts only for home games; away games check the opponent's stadium.
      // (Previously a dome-home team flagged every week, including road games
      // at outdoor stadiums.)
      const isHome = !matchupStr.startsWith("@");
      const isHomeDome = isHome && DOME_STADIUMS.has(stack.nflTeam);
      const isAwayDome = !isHome && DOME_STADIUMS.has(oppTeam.toUpperCase());

      if (isHomeDome || isAwayDome) {
        // Require bring-back: must have at least one WR/TE/RB from opposing team rostered
        const oppCatchers = bringBackPool.filter(p => p.team === oppTeam);
        if (oppCatchers.length === 0) continue;

        const domeLocation = isHomeDome ? stack.nflTeam : oppTeam;
        const allPlayers = [...stack.players, ...oppCatchers.map(toStackedPlayer)];
        const names = allPlayers.map(p => p.name).join(" + ");
        const wk = weekNums[weekIdx];

        domeStacks.push({
          type: "bring_back",
          nflTeam: stack.nflTeam,
          opposingTeam: oppTeam,
          players: allPlayers,
          label: `${stack.nflTeam} dome bring-back: ${names} @ ${domeLocation} (week ${wk})`,
          weekNum: wk,
        });
      }
    }
  }

  return domeStacks;
}

// ── Public API ────────────────────────────────────────────────────

/**
 * Analyse the stacking profile of a single uploaded team.
 */
export function detectTeamStacks(team: TeamDraft, schedule?: { weekNums?: number[]; weekSchedules?: Record<string, string[]> }): TeamStackResult {
  const { players } = team;

  const gameStacks = detectGameStacks(players);
  const bringBackStacks = detectPlayoffBringBackStacks(players, schedule?.weekNums, schedule?.weekSchedules);
  const domeStacks = detectPlayoffDomeStacks(players, schedule?.weekNums, schedule?.weekSchedules);

  // Orphan QB: QB in the roster with zero same-team skill players (WR/TE/RB)
  const qbs = players.filter(p => p.position === "QB" && p.team);
  const orphanQBs = qbs
    .filter(
      qb =>
        !players.some(
          p => STACK_PARTNER_POSITIONS.has(p.position) && p.team === qb.team
        )
    )
    .map(p => p.name);

  // Uncorrelated receivers: WR/TE/RB with no QB on the same team in this draft
  const qbTeams = new Set(qbs.map(q => q.team));
  const uncorrelatedReceivers = players
    .filter(
      p => STACK_PARTNER_POSITIONS.has(p.position) && p.team && !qbTeams.has(p.team)
    )
    .map(toStackedPlayer);

  const allStacks = [...gameStacks, ...bringBackStacks, ...domeStacks];
  const domeWeeks = [...new Set(domeStacks.map(s => s.weekNum!))].sort((a, b) => a - b);
  const domeStackCount = new Set(domeStacks.map(s => s.nflTeam)).size;

  return {
    draftId: team.draftId,
    stacks: allStacks,
    isOrphanQB: orphanQBs.length > 0,
    orphanQBs,
    uncorrelatedReceivers,
    gameStackCount: gameStacks.length,
    bringBackCount: bringBackStacks.length,
    domeStackCount,
    domeWeeks,
    runBackCount: 0,
    totalStackCount: allStacks.length,
  };
}

/**
 * Analyse stacking patterns across the entire uploaded portfolio.
 * Returns per-team results and portfolio-level aggregated metrics.
 */
export function analyzePortfolioStacks(teams: TeamDraft[], schedule?: { weekNums?: number[]; weekSchedules?: Record<string, string[]> }): PortfolioStackAnalysis {
  if (teams.length === 0) {
    return {
      totalTeams: 0,
      gameStackRate: 0,
      bringBackRate: 0,
      domeStackRate: 0,
      runBackRate: 0,
      orphanQBRate: 0,
      topPairings: [],
      teamResults: [],
    };
  }

  const teamResults = teams.map(team => detectTeamStacks(team, schedule));
  const totalTeams = teams.length;

  const teamsWithGameStack = teamResults.filter(r => r.gameStackCount > 0).length;
  const teamsWithBringBack = teamResults.filter(r => r.bringBackCount > 0).length;
  const teamsWithDomeStack = teamResults.filter(r => r.domeWeeks.includes(17)).length;
  const teamsWithOrphanQB = teamResults.filter(r => r.isOrphanQB).length;

  // Build QB × receiver pairing frequency map
  const pairingMap = new Map<string, { qbName: string; receiverName: string; nflTeam: string; count: number }>();

  for (const result of teamResults) {
    for (const stack of result.stacks) {
      if (stack.type !== "game_stack") continue;
      const qbPlayer = stack.players.find(p => p.position === "QB");
      if (!qbPlayer) continue;
      const receivers = stack.players.filter(p => STACK_PARTNER_POSITIONS.has(p.position));
      for (const recv of receivers) {
        const key = `${qbPlayer.name}|${recv.name}`;
        const existing = pairingMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          pairingMap.set(key, {
            qbName: qbPlayer.name,
            receiverName: recv.name,
            nflTeam: stack.nflTeam,
            count: 1,
          });
        }
      }
    }
  }

  const topPairings: StackPairingRate[] = Array.from(pairingMap.values())
    .map(p => ({ ...p, rate: p.count / totalTeams }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 30);

  return {
    totalTeams,
    gameStackRate: teamsWithGameStack / totalTeams,
    bringBackRate: teamsWithBringBack / totalTeams,
    domeStackRate: teamsWithDomeStack / totalTeams,
    runBackRate: 0,
    orphanQBRate: teamsWithOrphanQB / totalTeams,
    topPairings,
    teamResults,
  };
}
